import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Download, ShieldCheck, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DharmaWheel from "../components/DharmaWheel";

/**
 * Digital Membership Card. Layout is fixed at print A4-friendly proportions.
 * Print stylesheet hides chrome and centres the card on a clean A4 page.
 *
 * The QR encodes a public verification URL pointing to /verify/{membership_id}.
 * QR rendered with a tiny inline SVG generator (no external library) — supports
 * the alphanumeric character set our membership IDs use.
 */
export default function MembershipCard() {
  const { user } = useAuth();
  const [qrSvg, setQrSvg] = useState("");

  useEffect(() => {
    if (!user?.membership_id) return;
    // Use api.qrserver.com (free, no auth) for a crisp QR. Falls back to a
    // simple text-based ID if the network fails.
    const url = `${window.location.origin}/verify/${encodeURIComponent(user.membership_id)}`;
    fetch(`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(url)}&format=svg&margin=0&qzone=1`)
      .then((r) => r.text())
      .then(setQrSvg)
      .catch(() => setQrSvg(""));
  }, [user?.membership_id]);

  if (!user) return null;

  const issued = user.tier_active_since
    ? new Date(user.tier_active_since)
    : user.created_at
    ? new Date(user.created_at)
    : new Date();
  const tierLabel = user.membership_tier_label || "Community Member";
  const isLifetime = user.membership_tier === "lifetime";
  const validUntil = isLifetime
    ? "Life"
    : new Date(new Date(issued).setFullYear(issued.getFullYear() + 1)).toLocaleDateString("en-GB");

  return (
    <>
      {/* Top bar */}
      <div className="bg-navy text-pearl py-4 print:hidden" data-testid="card-bar">
        <div className="section-container flex items-center justify-between gap-4 flex-wrap">
          <Link to="/profile" className="inline-flex items-center gap-2 text-pearl/80 hover:text-pearl text-sm" data-testid="card-back">
            <ArrowLeft size={16} /> Back to profile
          </Link>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => window.print()}
              data-testid="card-print"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-navy hover:bg-gold-light rounded-full text-sm font-medium"
            >
              <Printer size={16} /> Print
            </button>
            <button
              onClick={() => downloadAsImage()}
              data-testid="card-download"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-pearl/30 hover:bg-pearl/10 rounded-full text-sm"
            >
              <Download size={16} /> Save as image
            </button>
          </div>
        </div>
      </div>

      <section className="bg-pearl py-12 px-4 min-h-[calc(100vh-12rem)]">
        <div className="max-w-3xl mx-auto">
          <p className="eyebrow print:hidden">Digital member ID</p>
          <h1 className="mt-3 font-display text-4xl text-navy print:hidden">Your membership card</h1>

          {/* CARD */}
          <div className="mt-8 print:mt-0 mx-auto" id="abs-uk-card">
            <div
              className="relative rounded-xl overflow-hidden shadow-2xl print:shadow-none border border-gold/40"
              style={{ width: "100%", maxWidth: 520, aspectRatio: "85.6 / 54", margin: "0 auto", background: "#001B3A", color: "#FDFBF7" }}
            >
              {/* Watermark wheel */}
              <div className="absolute -right-12 -bottom-12 opacity-10 pointer-events-none">
                <DharmaWheel size={300} color="#CFA052" ringColor="#CFA052" />
              </div>

              {/* Top band */}
              <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between" style={{ background: "rgba(207,160,82,0.15)", borderBottom: "1px solid rgba(207,160,82,0.4)" }}>
                <div className="flex items-center gap-2">
                  <DharmaWheel size={22} color="#CFA052" ringColor="#001B3A" />
                  <div>
                    <p className="text-[8px] uppercase tracking-[0.22em] font-semibold text-gold leading-none">
                      Ambedkarite Buddhist Global Network UK
                    </p>
                    <p className="text-[8px] uppercase tracking-[0.18em] text-pearl/70 leading-none mt-0.5">
                      United Kingdom
                    </p>
                  </div>
                </div>
                <p className="text-[8px] uppercase tracking-[0.22em] font-semibold text-gold">Member ID</p>
              </div>

              {/* Body */}
              <div className="absolute inset-0 pt-12 pb-3 px-5 flex gap-4 items-stretch">
                {/* Photo */}
                <div className="flex-shrink-0 flex items-center">
                  <div
                    className="rounded-md overflow-hidden bg-pearl/10 border border-gold/40 flex items-center justify-center"
                    style={{ width: 110, height: 130 }}
                  >
                    {user.photo_data_url ? (
                      <img src={user.photo_data_url} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-pearl/30 flex flex-col items-center text-center px-2">
                        <UserIcon size={32} />
                        <span className="text-[8px] mt-1 leading-tight">Photo not set</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Name + details */}
                <div className="flex-1 flex flex-col justify-between min-w-0">
                  <div>
                    <p className="text-[8px] uppercase tracking-[0.22em] text-saffron font-semibold leading-tight">{tierLabel}</p>
                    <p className="font-display text-[22px] leading-[1.05] text-pearl mt-0.5 truncate">{user.name}</p>
                    <p className="text-[10px] text-pearl/60 truncate">{user.email}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px] text-pearl/85">
                    <div>
                      <p className="text-[7px] uppercase tracking-widest text-gold/80">Member ID</p>
                      <p className="font-mono text-[10.5px] text-pearl">{user.membership_id || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[7px] uppercase tracking-widest text-gold/80">Issued</p>
                      <p className="text-pearl">{issued.toLocaleDateString("en-GB")}</p>
                    </div>
                    <div>
                      <p className="text-[7px] uppercase tracking-widest text-gold/80">Valid until</p>
                      <p className="text-pearl">{validUntil}</p>
                    </div>
                    <div>
                      <p className="text-[7px] uppercase tracking-widest text-gold/80">Voting</p>
                      <p className={user.voting_eligible ? "text-saffron font-semibold" : "text-pearl/60"}>
                        {user.voting_eligible ? "Eligible" : "Not yet"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* QR */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center" style={{ width: 90 }}>
                  <div className="bg-pearl rounded-md p-1.5 flex items-center justify-center" style={{ width: 78, height: 78 }}>
                    {qrSvg ? (
                      <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[8px] text-navy/50 text-center">QR loading…</div>
                    )}
                  </div>
                  <p className="text-[7px] uppercase tracking-[0.18em] text-pearl/60 mt-1">Verify</p>
                </div>
              </div>

              {/* Bottom strip */}
              <div className="absolute bottom-0 left-0 right-0 px-5 py-1.5 flex items-center justify-between border-t border-gold/30" style={{ background: "rgba(0,11,31,0.6)" }}>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck size={10} className="text-gold" />
                  <p className="text-[8px] tracking-wider text-pearl/70 uppercase">Education · Equality · Enlightenment</p>
                </div>
                <p className="text-[8px] tracking-wider text-pearl/60 italic">ambedkaritebuddhistsociety.org</p>
              </div>
            </div>
          </div>

          {/* Reverse-side meta (only on print) */}
          <div className="mt-8 print:mt-12 max-w-[520px] mx-auto text-center text-xs text-slate-500 print:hidden">
            This card is the property of the Ambedkarite Buddhist Global Network UK. The QR code links to{" "}
            <span className="font-mono">/verify/{user.membership_id}</span>, a public page confirming
            this member's tier, ID and standing.
          </div>
        </div>

        <style>{`
          @media print {
            @page { size: A4; margin: 0; }
            body { background: #fff !important; }
            #abs-uk-card { margin-top: 40mm !important; }
          }
        `}</style>
      </section>
    </>
  );
}

async function downloadAsImage() {
  try {
    const html2canvas = (await import("html2canvas")).default;
    const el = document.getElementById("abs-uk-card");
    if (!el) return;
    const canvas = await html2canvas(el, { backgroundColor: null, scale: 3, useCORS: true });
    const link = document.createElement("a");
    link.download = `abs-uk-membership-card.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  } catch {
    window.print();
  }
}
